import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';
import { socketClient } from '../index';

const THREAD_PAGE_SIZE = 30;
const MESSAGE_PAGE_SIZE = 50;
const REQUEST_TIMEOUT_MS = 3000;

const listenerRefs = {
  bound: false,
  handlers: {}
};

const dedupeThreads = (threads = []) => {
  const map = new Map();

  threads.forEach((thread) => {
    if (!thread || !thread.customer_id) {
      return;
    }

    map.set(thread.customer_id, thread);
  });

  return Array.from(map.values()).sort((a, b) => {
    const aId = Number(a.id || 0);
    const bId = Number(b.id || 0);
    return bId - aId;
  });
};

const dedupeMessages = (messages = []) => {
  const map = new Map();

  messages.forEach((message) => {
    if (!message || !message.id) {
      return;
    }

    map.set(message.id, message);
  });

  return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
};

const getIdTokenOrThrow = async () => {
  const user = firebase.auth().currentUser;

  if (!user) {
    throw new Error('Authentication is still loading. Please retry.');
  }

  return user.getIdToken();
};

const initialState = {
  customer: {},
  vehicles: [],
  invoices: [],
  appointments: [],
  messagesData: [],
  messagesList: [],
  loading: false,
  messageLoading: false,
  listLoading: false,
  loadingMoreThreads: false,
  loadingMoreMessages: false,
  listenersInitialized: false,
  activeCustomerId: null,
  searchTerm: '',
  filter: 'all',
  unreadCount: 0,
  listError: null,
  threadError: null,
  sendError: null,
  sending: false,
  threadListRequestAppend: false,
  threadListRequestBackground: false,
  threadRequestAppend: false,
  threadRequestBackground: false,
  threadRequestCustomerId: null,
  threadsPagination: {
    hasMore: false,
    nextCursor: null,
    limit: THREAD_PAGE_SIZE
  },
  threadPagination: {
    hasMore: false,
    nextCursor: null,
    limit: MESSAGE_PAGE_SIZE
  }
};

const slice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    listenersReady(state) {
      state.listenersInitialized = true;
    },
    listenersCleared(state) {
      state.listenersInitialized = false;
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload || '';
    },
    setFilter(state, action) {
      state.filter = action.payload || 'all';
    },
    setUnreadCount(state, action) {
      state.unreadCount = Number(action.payload || 0);
    },
    markThreadReadLocally(state, action) {
      const customerId = Number(action.payload || 0);
      const thread = state.messagesList.find(
        (item) => Number(item.customer_id) === customerId
      );

      if (!thread) {
        return;
      }

      const threadUnreadCount = Number(
        thread.unread_count !== undefined
          ? thread.unread_count
          : (thread.read === 0 ? 1 : 0)
      ) || 0;

      thread.unread_count = 0;
      thread.read = 1;
      state.unreadCount = Math.max(0, state.unreadCount - threadUnreadCount);
    },
    threadsRequestStarted(state, action) {
      const append = Boolean(action.payload?.append);
      const background = Boolean(action.payload?.background);
      state.threadListRequestAppend = append;
      state.threadListRequestBackground = background;
      state.listError = null;

      if (append) {
        state.loadingMoreThreads = true;
      } else if (background && state.messagesList.length > 0) {
        state.listLoading = false;
      } else {
        state.listLoading = true;
      }
    },
    threadsRequestFailed(state, action) {
      state.listLoading = false;
      state.loadingMoreThreads = false;
      state.threadListRequestAppend = false;
      if (!state.messagesList.length) {
        state.listError = action.payload || 'Unable to load conversations.';
      }
      state.threadListRequestBackground = false;
    },
    threadsReceived(state, action) {
      const payload = action.payload || {};
      const incomingThreads = Array.isArray(payload.threads)
        ? payload.threads
        : Array.isArray(payload.messagesList)
          ? payload.messagesList
          : [];

      const mergedThreads = state.threadListRequestAppend
        ? dedupeThreads([...state.messagesList, ...incomingThreads])
        : dedupeThreads(incomingThreads);

      state.messagesList = mergedThreads;
      state.threadsPagination = {
        hasMore: Boolean(payload.pagination?.hasMore),
        nextCursor: payload.pagination?.nextCursor || null,
        limit: Number(payload.pagination?.limit || THREAD_PAGE_SIZE)
      };

      if (payload.unreadCount !== undefined) {
        state.unreadCount = Number(payload.unreadCount || 0);
      }

      state.listLoading = false;
      state.loadingMoreThreads = false;
      state.threadListRequestAppend = false;
      state.threadListRequestBackground = false;
    },
    threadRequestStarted(state, action) {
      const append = Boolean(action.payload?.append);
      const background = Boolean(action.payload?.background);
      const customerId = Number(action.payload?.customerId || 0) || null;

      state.threadRequestAppend = append;
      state.threadRequestBackground = background;
      state.threadRequestCustomerId = customerId;
      state.threadError = null;

      if (customerId) {
        state.activeCustomerId = customerId;
      }

      if (append) {
        state.loadingMoreMessages = true;
      } else if (
        background &&
        state.messagesData.length > 0 &&
        Number(state.activeCustomerId || 0) === Number(customerId || 0)
      ) {
        state.messageLoading = false;
      } else {
        state.messageLoading = true;
      }
    },
    threadRequestFailed(state, action) {
      state.messageLoading = false;
      state.loadingMoreMessages = false;
      state.threadRequestAppend = false;
      state.threadRequestCustomerId = null;
      if (!(state.threadRequestBackground && state.messagesData.length > 0)) {
        state.threadError = action.payload || 'Unable to load conversation.';
      }
      state.threadRequestBackground = false;
    },
    threadReceived(state, action) {
      const payload = action.payload || {};
      const customer = payload.customer || null;

      if (!customer || !customer.id) {
        state.messageLoading = false;
        state.loadingMoreMessages = false;
        state.threadRequestAppend = false;
        state.threadRequestCustomerId = null;
        state.threadRequestBackground = false;
        return;
      }

      const customerId = Number(customer.id);

      if (state.activeCustomerId && customerId !== Number(state.activeCustomerId)) return;
      if (state.threadRequestCustomerId && customerId !== state.threadRequestCustomerId) {
        return;
      }

      const incomingMessages = Array.isArray(payload.messages) ? payload.messages : [];

      const nextMessages = state.threadRequestAppend
        ? dedupeMessages([...incomingMessages, ...state.messagesData])
        : dedupeMessages(incomingMessages);

      state.customer = customer;
      state.activeCustomerId = customerId;
      state.messagesData = nextMessages;
      state.vehicles = Array.isArray(customer.vehicles) ? customer.vehicles : [];
      state.appointments = state.vehicles.flatMap((vehicle) => vehicle.appointments || []);
      state.invoices = state.vehicles.flatMap((vehicle) => vehicle.invoices || []);
      state.threadPagination = {
        hasMore: Boolean(payload.pagination?.hasMore),
        nextCursor: payload.pagination?.nextCursor || null,
        limit: Number(payload.pagination?.limit || MESSAGE_PAGE_SIZE)
      };

      if (payload.unreadCount !== undefined) {
        state.unreadCount = Number(payload.unreadCount || 0);
      }

      state.messageLoading = false;
      state.loadingMoreMessages = false;
      state.threadRequestAppend = false;
      state.threadRequestCustomerId = null;
      state.threadRequestBackground = false;
    },
    legacyAllMessagesReceived(state, action) {
      const data = action.payload || {};
      state.customer = data.customer || {};
      state.messagesData = Array.isArray(data.messages) ? data.messages : [];
      state.messagesList = dedupeThreads(Array.isArray(data.messagesList) ? data.messagesList : []);
      state.vehicles = Array.isArray(data.customer?.vehicles) ? data.customer.vehicles : [];
      state.appointments = state.vehicles.flatMap((vehicle) => vehicle.appointments || []);
      state.invoices = state.vehicles.flatMap((vehicle) => vehicle.invoices || []);
      state.listLoading = false;
      state.messageLoading = false;
      state.loadingMoreThreads = false;
      state.loadingMoreMessages = false;
      state.threadListRequestAppend = false;
      state.threadListRequestBackground = false;
      state.threadRequestAppend = false;
      state.threadRequestBackground = false;
      state.threadRequestCustomerId = null;
      state.listError = null;
      state.threadError = null;

    },
    sendStarted(state) {
      state.sending = true;
      state.sendError = null;
    },
    sendFailed(state, action) {
      state.sending = false;
      state.sendError = action.payload || 'Failed to send message.';
    },
    sendFinished(state) {
      state.sending = false;
      state.sendError = null;
    }
  }
});

export const {
  listenersReady,
  listenersCleared,
  setSearchTerm,
  setFilter,
  setUnreadCount,
  markThreadReadLocally,
  threadsRequestStarted,
  threadsRequestFailed,
  threadsReceived,
  threadRequestStarted,
  threadRequestFailed,
  threadReceived,
  legacyAllMessagesReceived,
  sendStarted,
  sendFailed,
  sendFinished
} = slice.actions;

const safeSocketOff = (eventName, handler) => {
  if (!handler) {
    return;
  }

  socketClient.off(eventName, handler);
};

export const initializeSmsListeners = createAsyncThunk(
  'sms/initializeSmsListeners',
  async (_, { dispatch, getState }) => {
    if (!socketClient.socket || listenerRefs.bound) {
      return;
    }

    const onThreads = (payload) => {
      dispatch(threadsReceived(payload));

      const state = getState();
      const activeCustomerId = Number(state.sms.activeCustomerId || 0) || null;
      const loadedCustomerId = Number(state.sms.customer?.id || 0) || null;
      const activeThreadLoaded = Boolean(
        activeCustomerId &&
        loadedCustomerId === activeCustomerId &&
        state.sms.messagesData.length > 0
      );

      if (
        activeCustomerId &&
        !activeThreadLoaded &&
        !state.sms.messageLoading &&
        !state.sms.loadingMoreMessages
      ) {
        dispatch(requestThread({
          customerId: activeCustomerId,
          reset: true,
          markRead: true
        }));
      }
    };

    const onThread = (payload) => {
      dispatch(threadReceived(payload));
    };

    const onMarkRead = (payload) => {
      if (payload?.unreadCount !== undefined) {
        dispatch(setUnreadCount(payload.unreadCount));
      }
    };

    const onUnread = () => {
      // Ignore global unread broadcasts; they are not reliably shop-scoped.
      // We keep unread counts from scoped list/thread/markRead payloads.
    };

    const onLegacyAllMessages = (payload) => {
      const state = getState().sms;
      const hasLoadedModernData = state.messagesList.length > 0 && !state.listLoading && !state.loadingMoreThreads;

      if (hasLoadedModernData) {
        return;
      }

      dispatch(legacyAllMessagesReceived(payload));
    };

    const onLegacyCustomerMessages = (payload) => {
      const state = getState().sms;
      const activeCustomerId = Number(state.activeCustomerId || 0);
      const incomingCustomerId = Number(payload?.customer?.id || 0);
      const hasLoadedModernThread = state.messagesData.length > 0 && !state.messageLoading && !state.loadingMoreMessages;

      if (hasLoadedModernThread && activeCustomerId === incomingCustomerId) {
        return;
      }

      dispatch(threadReceived({
        customer: payload?.customer,
        messages: payload?.messages,
        pagination: {
          hasMore: false,
          nextCursor: null,
          limit: MESSAGE_PAGE_SIZE
        }
      }));
    };

    const onSocketError = (errorMessage) => {
      const state = getState().sms;
      const message = typeof errorMessage === 'string'
        ? errorMessage
        : (errorMessage?.message || 'Unable to load messages.');

      if (state.listLoading || state.loadingMoreThreads) {
        dispatch(threadsRequestFailed(message));
      } else if (state.messageLoading || state.loadingMoreMessages) {
        dispatch(threadRequestFailed(message));
      } else if (!state.messagesList.length) {
        dispatch(threadsRequestFailed(message));
      }
    };

    const onNewMessage = (payload) => {
      const activeCustomerId = getState().sms.activeCustomerId;
      const incomingCustomerId = Number(payload?.customer?.id || 0) || null;

      dispatch(requestThreads({ reset: true, background: true }));

      if (activeCustomerId && incomingCustomerId && Number(activeCustomerId) === incomingCustomerId) {
        dispatch(requestThread({
          customerId: activeCustomerId,
          reset: true,
          markRead: false,
          background: true
        }));
      }
    };

    listenerRefs.handlers = {
      onThreads,
      onThread,
      onMarkRead,
      onUnread,
      onLegacyAllMessages,
      onLegacyCustomerMessages,
      onNewMessage,
      onSocketError
    };

    safeSocketOff('messages:list', onThreads);
    safeSocketOff('messages:thread', onThread);
    safeSocketOff('messages:markRead', onMarkRead);
    safeSocketOff('getUnread', onUnread);
    safeSocketOff('getAllMessages', onLegacyAllMessages);
    safeSocketOff('getCustomerMessages', onLegacyCustomerMessages);
    safeSocketOff('newMessage', onNewMessage);
    safeSocketOff('error', onSocketError);

    socketClient.on('messages:list', onThreads);
    socketClient.on('messages:thread', onThread);
    socketClient.on('messages:markRead', onMarkRead);
    socketClient.on('getUnread', onUnread);
    socketClient.on('getAllMessages', onLegacyAllMessages);
    socketClient.on('getCustomerMessages', onLegacyCustomerMessages);
    socketClient.on('newMessage', onNewMessage);
    socketClient.on('error', onSocketError);

    listenerRefs.bound = true;
    dispatch(listenersReady());
  }
);

export const cleanupSmsListeners = createAsyncThunk(
  'sms/cleanupSmsListeners',
  async (_, { dispatch }) => {
    if (!listenerRefs.bound) {
      return;
    }

    safeSocketOff('messages:list', listenerRefs.handlers.onThreads);
    safeSocketOff('messages:thread', listenerRefs.handlers.onThread);
    safeSocketOff('messages:markRead', listenerRefs.handlers.onMarkRead);
    safeSocketOff('getUnread', listenerRefs.handlers.onUnread);
    safeSocketOff('getAllMessages', listenerRefs.handlers.onLegacyAllMessages);
    safeSocketOff('getCustomerMessages', listenerRefs.handlers.onLegacyCustomerMessages);
    safeSocketOff('newMessage', listenerRefs.handlers.onNewMessage);
    safeSocketOff('error', listenerRefs.handlers.onSocketError);

    listenerRefs.bound = false;
    listenerRefs.handlers = {};
    dispatch(listenersCleared());
  }
);

export const requestThreads = createAsyncThunk(
  'sms/requestThreads',
  async (payload = {}, { dispatch, getState }) => {
    const state = getState().sms;

    if (!socketClient.socket) {
      dispatch(threadsRequestFailed('Socket connection is not available.'));
      return;
    }

    try {
      const idToken = await getIdTokenOrThrow();
      const append = Boolean(payload.append && !payload.reset);
      const background = Boolean(payload.background);
      const cursor = append ? state.threadsPagination.nextCursor : null;
      const searchTerm = payload.searchTerm !== undefined ? payload.searchTerm : state.searchTerm;
      const filter = payload.filter || state.filter;

      dispatch(threadsRequestStarted({ append, background }));

      socketClient.emit('messages:list', {
        idToken,
        cursor,
        limit: payload.limit || THREAD_PAGE_SIZE,
        filter,
        searchTerm
      });

      setTimeout(() => {
        const smsState = getState().sms;
        const isStillLoading = append ? smsState.loadingMoreThreads : smsState.listLoading;

        if (isStillLoading) {
          if (!smsState.messagesList.length) {
            socketClient.emit('getAllMessages', {
              idToken,
              startingId: smsState.activeCustomerId || undefined
            });
          }

          setTimeout(() => {
            const timeoutState = getState().sms;
            const stillLoadingAfterFallback = append ? timeoutState.loadingMoreThreads : timeoutState.listLoading;

            if (stillLoadingAfterFallback) {
              dispatch(threadsRequestFailed('Loading conversations timed out. Please retry.'));
            }
          }, REQUEST_TIMEOUT_MS);
        }
      }, REQUEST_TIMEOUT_MS);
    } catch (error) {
      dispatch(threadsRequestFailed(error?.message || 'Unable to load conversations.'));
    }
  }
);

export const requestThread = createAsyncThunk(
  'sms/requestThread',
  async (payload, { dispatch, getState }) => {
    if (!payload?.customerId) {
      dispatch(threadRequestFailed('A customer is required.'));
      return;
    }

    if (!socketClient.socket) {
      dispatch(threadRequestFailed('Socket connection is not available.'));
      return;
    }

    try {
      const state = getState().sms;
      const append = Boolean(payload.append && !payload.reset);
      const background = Boolean(payload.background);
      const beforeId = append ? state.threadPagination.nextCursor : null;

      dispatch(threadRequestStarted({
        append,
        customerId: payload.customerId,
        background
      }));

      const idToken = await getIdTokenOrThrow();
      if (Number(getState().sms.activeCustomerId) !== Number(payload.customerId)) return;
      socketClient.emit('messages:thread', {
        idToken,
        customer_id: payload.customerId,
        before_id: beforeId,
        limit: payload.limit || MESSAGE_PAGE_SIZE,
        mark_read: payload.markRead !== false
      });

      setTimeout(() => {
        const smsState = getState().sms;
        const isStillLoading = append ? smsState.loadingMoreMessages : smsState.messageLoading;
        const sameCustomer = Number(smsState.threadRequestCustomerId || 0) === Number(payload.customerId || 0);

        if (isStillLoading && sameCustomer) {
          if (!smsState.messagesData.length) {
            socketClient.emit('getCustomerMessages', {
              idToken,
              customer_id: payload.customerId
            });
          }

          setTimeout(() => {
            const timeoutState = getState().sms;
            const stillLoadingAfterFallback = append ? timeoutState.loadingMoreMessages : timeoutState.messageLoading;
            const stillSameCustomer = Number(timeoutState.threadRequestCustomerId || 0) === Number(payload.customerId || 0);

            if (stillLoadingAfterFallback && stillSameCustomer) {
              dispatch(threadRequestFailed('Loading conversation timed out. Please retry.'));
            }
          }, REQUEST_TIMEOUT_MS);
        }
      }, REQUEST_TIMEOUT_MS);
    } catch (error) {
      if (Number(getState().sms.activeCustomerId) === Number(payload.customerId)) {
        dispatch(threadRequestFailed(error?.message || 'Unable to load conversation.'));
      }
    }
  }
);

export const requestSearchMessages = createAsyncThunk(
  'sms/requestSearchMessages',
  async (searchTerm, { dispatch }) => {
    dispatch(setSearchTerm(searchTerm || ''));
    dispatch(requestThreads({ reset: true, searchTerm: searchTerm || '' }));
  }
);

export const requestSendMessage = createAsyncThunk(
  'sms/requestSendMessage',
  async (payload, { dispatch, getState }) => {
    const customer = getState().sms.customer;
    const message = typeof payload === 'string' ? payload : payload?.message;
    const attachments = Array.isArray(payload?.attachments) ? payload.attachments : [];
    const trimmedMessage = String(message || '').trim();

    if (!customer?.id || (!trimmedMessage && !attachments.length)) {
      return;
    }

    dispatch(sendStarted());

    try {
      const idToken = await getIdTokenOrThrow();
      await axios.post(buildApiUrl('/message/send'), {
        customer,
        message: trimmedMessage,
        attachments
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      dispatch(sendFinished());
      dispatch(requestThread({ customerId: customer.id, reset: true, markRead: true }));
      dispatch(requestThreads({ reset: true }));
    } catch (error) {
      dispatch(sendFailed(error?.response?.data?.error || error?.message || 'Failed to send message.'));
    }
  }
);

export const requestOptimizeMessageDraft = createAsyncThunk(
  'sms/requestOptimizeMessageDraft',
  async (message, { getState }) => {
    const customer = getState().sms.customer;
    const trimmedMessage = String(message || '').trim();

    if (!customer?.id || !trimmedMessage) {
      throw new Error('A customer and message draft are required.');
    }

    const idToken = await getIdTokenOrThrow();
    const response = await axios.post(buildApiUrl('/message/optimize'), {
      customer,
      message: trimmedMessage
    }, {
      headers: { Authorization: `Bearer ${idToken}` }
    });

    return String(response?.data?.optimizedMessage || trimmedMessage).trim() || trimmedMessage;
  }
);

export const requestMarkThreadRead = createAsyncThunk(
  'sms/requestMarkThreadRead',
  async (customerId, { dispatch, getState }) => {
    if (!customerId || !socketClient.socket) {
      return null;
    }

    const idToken = await getIdTokenOrThrow();
    const unreadCount = getState().sms.unreadCount;
    dispatch(markThreadReadLocally(customerId));

    socketClient.emit('messages:markRead', {
      idToken,
      customer_id: customerId
    });

    return unreadCount;
  }
);

// Backward compatibility wrappers
export const requestAllMessages = (payload = {}) => requestThreads({ reset: true, ...payload });
export const fetchAllMessages = () => initializeSmsListeners();
export const requestCustomerMessage = (customerId) => requestThread({ customerId, reset: true, markRead: true });
export const fetchCustomerMessages = () => () => Promise.resolve();
export const fetchSearchMessages = () => () => Promise.resolve();
export const fetchNewMessage = () => () => Promise.resolve();

export const reducer = slice.reducer;
